import type { PropsWithChildren } from "react";
import {
  NodeViewWrapper,
  NodeViewContent,
  ReactNodeViewRenderer,
  NodeViewProps,
} from "@tiptap/react";
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight";
import { common, createLowlight } from "lowlight";
import upperFirst from "lodash/upperFirst";
import { ClipboardCopyIcon, CheckIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCopyText } from "@/hooks/navigator";

const lowlight = createLowlight(common);

function CodeBlockWrapper({ children }: PropsWithChildren) {
  return (
    <div className="bg-gray-200 rounded-lg overflow-hidden mb-6">
      {children}
    </div>
  );
}

const CodeBlockComponent = ({
  node: {
    textContent,
    attrs: { language: defaultLanguage },
  },
  updateAttributes,
  extension,
}: NodeViewProps) => {
  const [isCodeCopied, copyCode] = useCopyText();

  return (
    <NodeViewWrapper as={CodeBlockWrapper}>
      <div className="flex p-3.5">
        <div className="mr-auto">
          <Select
            name="codeLng"
            defaultValue={defaultLanguage}
            onValueChange={(value) => updateAttributes({ language: value })}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Language" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">auto</SelectItem>
              {extension.options.lowlight
                .listLanguages()
                .map((lang: string) => (
                  <SelectItem key={lang} value={lang}>
                    {upperFirst(lang)}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="xs"
              variant="link"
              onClick={() => copyCode(textContent || "")}
              className="ml-auto px-1 text-gray-900"
            >
              {isCodeCopied ? <CheckIcon /> : <ClipboardCopyIcon />}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p>Copy code</p>
          </TooltipContent>
        </Tooltip>
      </div>
      <pre className="font-mono">
        <NodeViewContent as="code" />
      </pre>
    </NodeViewWrapper>
  );
};

export const CodeBlockExtension = CodeBlockLowlight.extend({
  addNodeView() {
    return ReactNodeViewRenderer(CodeBlockComponent);
  },
}).configure({ lowlight });
